import { useMutation, useQuery, useQueries } from '@tanstack/react-query-v4';
import { useMemo, useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import apiFetch, { ApiFetchOptions, HTTPError } from 'react-scale/lib/api-fetch';
import {
  BatchTaskStatusRequest,
  DeliveryBatchDTO,
  TaskIdsDeliveryRequest,
  ManualUploadDeliveryRequest,
  DeliveryFileFormat,
  TasksResult,
  ManualUploadResponse,
  UserInfoByEmailResponse,
  MoveUsersToTeamRequest,
  MoveUsersToTeamResponse,
  TeamMembersResponse,
  DeliveryPreviewRequest,
  DeliveriesPageResult,
  DeliveryJobProgress,
  TaskInfoDTO,
  DeliveryJobProgressInfo,
} from '@scale/shared/chatDeliveryBuilder';
import { queryFactory } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { tryParseErrorResponse } from 'react-scale/lib/errorMessages';
import EnrichedError from 'react-scale/lib/enrichedError';
import qs from 'query-string';
import {
  DeliveryValidationRequest,
  DeliveryValidationOverrideRequest,
  IDeliveryValidationResponse,
} from '@scale/shared/types/deliveryValidations';
import { ReviewLevel } from '@scale/shared/defaults';
import { TaskRollupHumanNodeStatus } from '@scale/shared/pipelinev3/humanNodeStatus';
import { useGetEvalRunInfo } from 'react-scale/corp/views/modelEvalTools/data/modelEvalCorpClient';
import { CustomerBatchExtrasRecord } from '@scale/customer-app-shared';

export async function deliveryApiFetch<R = any, B = any>(
  url: string,
  options: ApiFetchOptions<B> = {},
): Promise<R> {
  try {
    return await apiFetch<R, B>(null, url, options);
  } catch (err) {
    if (err instanceof HTTPError) {
      // see tryParseErrorResponse() for how err.response.error gets populated
      throw new EnrichedError(
        err.status ?? 400,
        err.response.error,
        err.response.error,
        err.response.error,
        url,
      );
    } else {
      throw err;
    }
  }
}

const TASK_IDS_CHUNK_SIZE = 5000;
export function useCollectTaskStatusByTaskIdsQueries(): {
  isLoading: boolean;
  collectTaskStatuses: (
    params: { projectId: string; taskIds: string[] },
    options: { onSuccess: (result: TasksResult) => void; onError: () => void },
  ) => void;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [taskIds, setTaskIds] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [options, setOptions] = useState<{
    onSuccess: (result: TasksResult) => void;
    onError: () => void;
  } | null>(null);

  const collectTaskStatuses = useCallback(
    (
      params: { projectId: string; taskIds: string[] },
      options: { onSuccess: (result: TasksResult) => void; onError: () => void },
    ) => {
      const { projectId, taskIds } = params;
      setIsLoading(true);
      setProjectId(projectId);
      setOptions(options);
      setTaskIds(taskIds);
    },
    [],
  );

  const queries = useMemo(() => {
    if (taskIds.length === 0) return [];

    const taskIdsChunks = _.chunk(taskIds, TASK_IDS_CHUNK_SIZE);
    return taskIdsChunks.map(taskIdsChunk => ({
      queryKey: ['collectTaskStatusByTaskIds', projectId, taskIdsChunk] as const,
      queryFn: async (): Promise<TasksResult> => {
        return await deliveryApiFetch(`corp-api/delivery-builder/tasks/select_by_tasks`, {
          method: 'POST',
          body: { projectId, taskIds: taskIdsChunk },
        });
      },
      enabled: true,
    }));
  }, [projectId, taskIds]);

  const results = useQueries({ queries });

  useEffect(() => {
    if (!isLoading) return;

    const isResultsLoading = results.some(result => result.isLoading);
    if (isResultsLoading) {
      setIsLoading(true);
      return;
    } else {
      setIsLoading(false);
    }

    const isError = results.some(result => result.isError);
    if (isError) {
      options?.onError();
      return;
    }

    // Check if any chunk returned a pipeline version error
    const pipelineVersionErrors = results
      .map(result => result.data?.pipelineVersionError)
      .filter(Boolean);

    // If any chunk has a pipeline version error, combine all the errors
    const combinedPipelineVersionError =
      pipelineVersionErrors.length > 0
        ? {
            pipelineV2TaskIds: _.flatten(pipelineVersionErrors.map(e => e!.pipelineV2TaskIds)),
            pipelineV3TaskIds: _.flatten(pipelineVersionErrors.map(e => e!.pipelineV3TaskIds)),
          }
        : undefined;

    // Combine all successful results
    const combinedResult: TasksResult = {
      tasks: _.flatten(results.map(result => result.data?.tasks!)),
      statusMap: results
        .map(result => result.data?.statusMap!)
        .reduce(
          (acc, curr) =>
            _.mergeWith(acc, curr, (a: string[] | undefined, b: string[]) => (a ? a.concat(b) : b)),
          {
            pendingIdsWithAttempts: [],
            completedIds: [],
            pendingIdsWithNoAttempts: [],
            nonExistingIds: [],
            otherStatusIds: [],
            pendingIdsWithNoResponse: [],
            pendingIdsWithOversizedResponse: [],
          },
        ),
      pipelineVersionError: combinedPipelineVersionError,
    };
    options?.onSuccess?.(combinedResult);
  }, [isLoading, !results.some(result => result.isLoading)]);

  return { collectTaskStatuses, isLoading };
}

export function useCollectTaskStatusByBatchId() {
  return useMutation<TasksResult & { warning?: string }, string, BatchTaskStatusRequest>({
    mutationFn: async ({ projectId, batchId }) => {
      return await deliveryApiFetch(`corp-api/delivery-builder/tasks/select_by_batch`, {
        method: 'POST',
        body: { projectId, batchId },
      });
    },
  });
}

export function useCustomerIdOfProjectQuery(projectId: string) {
  return useQuery<string>({
    queryKey: [...queryFactory.projects.all, 'deliveryCustomerId', projectId] as const,
    queryFn: async () => {
      const { customerId } = await deliveryApiFetch(
        `corp-api/delivery-builder/projects/customer_id?projectId=${projectId}`,
      );
      return customerId;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useTeamMembersOfProjectQuery(
  projectId: string,
  onSuccess?: (data: TeamMembersResponse) => void,
) {
  return useQuery<TeamMembersResponse>({
    queryKey: [...queryFactory.projects.all, 'deliveryTeamMembers', projectId] as const,
    queryFn: async () => {
      return await deliveryApiFetch(
        `corp-api/delivery-builder/projects/team_members?projectId=${projectId}`,
      );
    },
    staleTime: 5 * 60 * 1000,
    ...(onSuccess && { onSuccess }),
  });
}

export function useUserInfoByEmailQuery(
  emails: string[],
  onSuccess?: (data: UserInfoByEmailResponse) => void,
) {
  return useQuery<UserInfoByEmailResponse>({
    queryKey: [...queryFactory.projects.all, 'deliveryUserInfo', emails.join(',')] as const,
    queryFn: async () => {
      return await deliveryApiFetch(`corp-api/delivery-builder/users/info_by_email`, {
        method: 'GET',
        query: { emails },
      });
    },
    ...(onSuccess && { onSuccess }),
  });
}

export function useMoveUsersToTeamMutation() {
  return useMutation<MoveUsersToTeamResponse, string, MoveUsersToTeamRequest>({
    mutationFn: async body => {
      return await deliveryApiFetch(`corp-api/delivery-builder/users/move_to_team`, {
        method: 'POST',
        body,
      });
    },
  });
}

export function useRenameDeliveriesMutation() {
  return useMutation<any, string, { projectId: string; currentName: string; newName: string }>({
    mutationFn: async ({ projectId, currentName, newName }) => {
      return await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/rename_deliveries`,
        {
          method: 'POST',
          body: { currentName, newName, projectId },
        },
      );
    },
  });
}

export function useGetDeliveryValidationSetQuery(deliveryId: string) {
  return useQuery<IDeliveryValidationResponse>({
    queryKey: ['getDeliveryValidationSet', deliveryId],
    queryFn: async () => {
      return await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryId}/validation_set`,
        {
          method: 'GET',
        },
      );
    },
    staleTime: 10 * 1000,
  });
}

export function usePreviewDeliveryMutation() {
  return useMutation<{ arn: string }, string, DeliveryPreviewRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/run_preview`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
  });
}

// Types for delivery push preview
interface DeliveryPushPreviewRequest {
  projectId: string;
  deliveryIds: string[];
  referenceDate?: string; // Optional: ISO date string for delivery date calculation
  newTasksAreSamples?: boolean;
}

// Matches server response from /corp/task-delivery/preview
interface DeliveryPushPreviewFilePreview {
  taskIds: string[];
  sampleData?: Record<string, unknown>[];
}

export interface DeliveryPushPreviewUploadedFile {
  path: string;
  bucket: string;
  taskCount: number;
  uploadedAt?: string;
  /** Language code for the file (e.g., "en_US", "es_ES") */
  languageCode?: string;
  /** Preview data available in dry run mode - contains task IDs and full formatted data */
  preview?: DeliveryPushPreviewFilePreview;
}

interface DeliveryPushPreviewFailedFile {
  path: string;
  taskIds: string[];
  error: string;
}

interface DeliveryPushPreviewSummary {
  totalFiles: number;
  totalTasks: number;
  successCount: number;
  failureCount: number;
  dryRun: boolean;
}

interface DeliveryPushPreviewResult {
  uploaded: DeliveryPushPreviewUploadedFile[];
  failed: DeliveryPushPreviewFailedFile[];
  summary: DeliveryPushPreviewSummary;
}

interface DeliveryPushEligibleTasks {
  total: number;
  firstTimeCount: number; // First time this task is being delivered
  redeliveryCount: number; // Task is being delivered again
}

interface DeliveryPushConfig {
  customer: string;
  bucket: string;
  maxTasksPerFile: number;
  fileFormat: string;
}

// FNA1-specific: Warning for tasks missing formatted data
interface MissingFormattedData {
  count: number;
  totalEligible: number;
  taskIds: string[];
  message: string;
}

export interface DeliveryPushPreviewResponse {
  preview: DeliveryPushPreviewResult;
  eligibleTasks: DeliveryPushEligibleTasks;
  config: DeliveryPushConfig;
  // FNA1-specific: Warning for tasks missing formatted data (DeliveredTaskResponse)
  missingFormattedData?: MissingFormattedData;
}

export function usePreviewDeliveryPushMutation() {
  return useMutation<DeliveryPushPreviewResponse, Error, DeliveryPushPreviewRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(`corp-api/task-delivery/preview`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

// Execute delivery push request (same as preview)
type DeliveryPushExecuteRequest = DeliveryPushPreviewRequest;

// Execute delivery push response
interface DeliveryPushExecuteResponse {
  result: DeliveryPushPreviewResult;
  eligibleTasks: DeliveryPushEligibleTasks;
  config: {
    customer: string;
    bucket: string;
    maxTasksPerFile: number;
    fileFormat: string;
  };
}

export function useExecuteDeliveryPushMutation() {
  return useMutation<DeliveryPushExecuteResponse, Error, DeliveryPushExecuteRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(`corp-api/task-delivery/execute`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

export function useSendCsvSampleMutation() {
  return useMutation<{ path: string }, Error, { projectId: string; deliveryId: string }>({
    mutationFn: async body => {
      return deliveryApiFetch('corp-api/task-delivery/send-csv-sample', {
        method: 'POST',
        body,
      });
    },
  });
}

// Response type for unpushed tasks endpoint
interface UnpushedTasksResponse {
  deliveryId: string;
  totalTasks: number;
  pushedTaskIds: string[];
  unpushedTaskIds: string[];
  pushedCount: number;
  unpushedCount: number;
}

/**
 * Fetch unpushed tasks for a delivery
 * Used to identify which tasks failed in a partially pushed delivery
 */
export function useGetUnpushedTasksQuery(deliveryId: string, enabled: boolean = true) {
  return useQuery<UnpushedTasksResponse, Error>({
    queryKey: ['unpushedTasks', deliveryId],
    queryFn: async () => {
      const result = await deliveryApiFetch(`corp-api/task-delivery/${deliveryId}/unpushed-tasks`, {
        method: 'GET',
      });
      return result;
    },
    enabled: enabled && !!deliveryId,
  });
}

export function useCreateDeliveryMutation() {
  return useMutation<any, string, TaskIdsDeliveryRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(`corp-api/delivery-builder/delivery_batches`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

export function useCreateMultipartDeliveryMutation() {
  return useMutation<any, string, TaskIdsDeliveryRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/multipart`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
  });
}

export function useRunDeliveryValidationMutation() {
  return useMutation<any, string, DeliveryValidationRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(`corp-api/delivery-builder/delivery_batches/validate`, {
        method: 'POST',
        body,
      });
      return result;
    },
  });
}

export function useFetchDeliveryValidationSetMutation() {
  return useMutation<any, string, { id: string }>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/get_validation_set_by_id`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
  });
}
export function useRequestDeliveryValidationOverrideMutation(onSuccess?: () => void) {
  return useMutation<any, string, DeliveryValidationOverrideRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/request_override`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
    onSuccess,
  });
}

export function useRequestDeliveryValidationOverrideByIdMutation(onSuccess?: () => void) {
  return useMutation<any, string, { validationId: string }>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/request_override_by_id`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
    onSuccess,
  });
}

export function useOverrideFailedValidationAndForceSendDelivery() {
  return useMutation<
    {},
    Error,
    { deliveryBatchId: string; deliveryValidationId: string; justification: string }
  >({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/override_validation_and_force_send_delivery`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
  });
}

export function useCreateManualUploadDeliveryMutation() {
  return useMutation<ManualUploadResponse, string, ManualUploadDeliveryRequest>({
    mutationFn: async body => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/manual_upload`,
        {
          method: 'POST',
          body,
        },
      );
      return result;
    },
  });
}

export function useRetryJobMutation() {
  return useMutation<any, string, { deliveryBatchId: string }>({
    mutationFn: async ({ deliveryBatchId }) => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryBatchId}/retry`,
        {
          method: 'POST',
        },
      );
      return result;
    },
  });
}

export function useRetryExternalUploadMutation() {
  return useMutation<any, string, { deliveryBatchId: string }>({
    mutationFn: async ({ deliveryBatchId }) => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryBatchId}/retry-external-upload`,
        {
          method: 'POST',
        },
      );
      return result;
    },
  });
}

export function useQueryDeliveryJobProgress(deliveryBatchId: string) {
  return useQuery<DeliveryJobProgressInfo>({
    queryKey: ['queryTaskStatusInDeliveryJob', deliveryBatchId],
    queryFn: async () => {
      return await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryBatchId}/job_progress`,
      );
    },
    refetchInterval: 20000, // 20 seconds
  });
}

export function useQueryDeliveryJobProgressV2(deliveryBatchId: string) {
  return useQuery<DeliveryJobProgress>({
    queryKey: ['queryTaskStatusInDeliveryJobV2', deliveryBatchId],
    queryFn: async () => {
      return await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryBatchId}/job_progress_v2`,
      );
    },
    refetchInterval: 20000, // 20 seconds
  });
}

export function useUpdateIsTestMutation() {
  return useMutation<
    any,
    string,
    { deliveryId: string; newIsTest: boolean; receiverEmails?: string[] }
  >({
    mutationFn: async ({ deliveryId, newIsTest, receiverEmails }) => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/${deliveryId}/is_test`,
        {
          method: 'POST',
          body: { isTest: newIsTest, receiverEmails },
        },
      );
      return result;
    },
  });
}

export function useGetDeliveriesPaged({
  projectId,
  maxEpochMillis,
}: {
  projectId: string;
  maxEpochMillis?: number;
}) {
  return useQuery<DeliveriesPageResult>({
    queryKey: [
      ...queryFactory.projects.all,
      'getDeliveryBatchesPaged',
      projectId,
      maxEpochMillis,
    ] as const,
    queryFn: async () => {
      // Library omits undefined keys by default
      const queryString = qs.stringify({
        projectId,
        maxEpochMillis,
      });

      return await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/paged?${queryString}`,
      );
    },
  });
}

export function useGetAllDeliveries({ projectId }: { projectId: string }) {
  return useQuery<DeliveryBatchDTO[]>({
    queryKey: [...queryFactory.projects.all, 'getAllDeliveryBatches', projectId] as const,
    queryFn: async () => {
      // Library omits undefined keys by default
      const queryString = qs.stringify({
        projectId,
      });

      return await deliveryApiFetch(`corp-api/delivery-builder/delivery_batches?${queryString}`);
    },
    staleTime: 0, // immediately mark data as stale
    cacheTime: 1000 * 30, // keep data cached for 30 seconds
    refetchOnMount: 'always',
  });
}

export function useGetDeliveriesByTaskId({
  projectId,
  taskId,
}: {
  projectId: string;
  taskId: string;
}) {
  return useQuery<DeliveryBatchDTO[]>({
    queryKey: [
      ...queryFactory.projects.all,
      'getDeliveryBatchesByTaskId',
      projectId,
      taskId,
    ] as const,
    queryFn: async () => {
      const queryString = qs.stringify({
        projectId,
        taskId,
      });

      const { deliveries } = await deliveryApiFetch(
        `corp-api/delivery-builder/fetch_delivery_by_task_id?${queryString}`,
      );
      return deliveries;
    },
    enabled: !!taskId,
  });
}

const DELIVERIES_REFRESH_INTERVAL = 1000 * 10;

export const getRecentlyUpdatedDeliveriesKey = (projectId: string) => [
  ...queryFactory.projects.all,
  'getDeliveryBatchesRecentlyUpdated',
  projectId,
];

export function useGetRecentlyUpdatedDeliveriesQuery({
  projectId,
  maxEpochMillis,
}: {
  projectId: string;
  maxEpochMillis?: number;
}) {
  return useQuery<DeliveryBatchDTO[]>({
    queryKey: [...getRecentlyUpdatedDeliveriesKey(projectId), maxEpochMillis],
    queryFn: async () => {
      // Library omits undefined keys by default
      const queryString = qs.stringify({
        projectId,
        maxEpochMillis,
      });

      const { deliveries } = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/recently_updated?${queryString}`,
      );
      return deliveries;
    },
    refetchOnMount: false,
    refetchInterval: DELIVERIES_REFRESH_INTERVAL,
  });
}

export function useCreatePresignedUploadUrlMutation() {
  return useMutation<
    any,
    string,
    { projectId: string; fileName: string; fileFormat: DeliveryFileFormat }
  >({
    mutationFn: async ({ projectId, fileName, fileFormat }) => {
      const result = await deliveryApiFetch(
        `corp-api/delivery-builder/delivery_batches/manual_upload_url`,
        {
          method: 'POST',
          body: { projectId, fileName, fileFormat },
        },
      );
      return result;
    },
  });
}

export function useUploadToPresignedUrlMutation() {
  return useMutation<string, Error, { url: string; fields: Record<string, any>; fileObject: File }>(
    {
      mutationFn: async ({ url, fields, fileObject }) => {
        const formData = new FormData();
        for (const [key, val] of Object.entries(fields)) {
          formData.append(key, val as string);
        }
        formData.append('file', fileObject);

        const res = await fetch(url, { method: 'POST', body: formData });
        // Mimic apiFetch helper behavior
        if (!res.ok) {
          throw new HTTPError(res.statusText, await tryParseErrorResponse(res));
        }

        // Can't use res.headers.get('Location') because it's not allowed by CORS
        const s3Url = `${url}/${fields.key}`;
        return s3Url;
      },
    },
  );
}

export function useV3TaskCompletionHooksQuery(projectId: string) {
  return useQuery<string[]>({
    queryKey: [...queryFactory.projects.all, 'v3TaskCompletionHooks', projectId] as const,
    queryFn: async () => {
      const { hookNames } = await deliveryApiFetch(
        `corp-api/delivery-builder/projects/v3_completion_hooks?projectId=${projectId}`,
      );
      return hookNames;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useGetFilteredTasksOfProjectQuery(
  projectId: string,
  reviewLevel: ReviewLevel,
  humanNodeStatus: TaskRollupHumanNodeStatus,
) {
  return useQuery<TaskInfoDTO[]>({
    queryKey: ['getFilteredTasksOfProject', projectId],
    queryFn: async () => {
      return await deliveryApiFetch(`corp-api/delivery-builder/tasks/select_by_filters`, {
        query: { projectId, reviewLevel, humanNodeStatus },
      });
    },
  });
}

export function useGetProjectFollowers(projectId: string | undefined) {
  return useQuery<{ followerEmails: string[] }>({
    queryKey: ['projectFollowers', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const url = `/internal/projects/${projectId}/projectFollowers`;
      return await deliveryApiFetch(url, { method: 'GET' });
    },
    staleTime: 0, // immediately mark data as stale
    cacheTime: 1000 * 30, // keep data cached for 30 seconds
    refetchOnMount: 'always',
  });
}

export function useExternalDeliveryTrackerQuery(deliveryId: string, projectId: string) {
  return useQuery<{ externalDeliveryName?: string; url?: string; metadata?: Record<string, any> }>({
    queryKey: ['externalDeliveryTracker', deliveryId, projectId] as const,
    queryFn: async () => {
      const url = `corp-api/delivery-builder/external_delivery_tracker/${deliveryId}?projectId=${projectId}`;
      return await deliveryApiFetch(url, { method: 'GET' });
    },
    enabled: !!deliveryId && !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

function useBatchExtrasQueries(batchIdsRaw: string[]) {
  const batchIds = useMemo(() => _.uniq(batchIdsRaw), [batchIdsRaw]);

  const results = useQueries<CustomerBatchExtrasRecord[]>({
    queries: batchIds.map(id => ({
      queryKey: ['batches', 'extras', id],
      queryFn: async () =>
        await deliveryApiFetch<CustomerBatchExtrasRecord>(`/internal/customer_app/batches/extras`, {
          method: 'POST',
          body: {
            batchIds: [id],
          },
        }),
      staleTime: 1000 * 60 * 10,
      refetchOnMount: false,
    })),
  });

  const {
    data,
    idToIsLoading,
  }: { data: CustomerBatchExtrasRecord; idToIsLoading: Record<string, boolean> } = useMemo(() => {
    const idToIsLoading = Object.fromEntries(
      results.map((result, idx) => [batchIds[idx], result.isLoading]),
    );

    return {
      data: _.assign({}, ...results.map(result => result.data)),
      idToIsLoading,
    };
  }, [batchIds, results]);

  return { data, idToIsLoading };
}

function useGetTestCasesInfo(runId?: string) {
  return useQuery<{ totalNumTasks: number; numValidTasks: number; numInvalidTasks: number }>({
    queryKey: ['evalRuns', 'testCasesInfo', runId],
    queryFn: async () => {
      const url = `internal/self_serve/modelEvalAnalytics/testCasesInfo`;
      return await deliveryApiFetch(url, { method: 'GET', query: { runId } });
    },
    enabled: !!runId,
  });
}

export function useCachedEvaluationInfo({ runId }: { runId?: string }) {
  const { data: runInfo } = useGetEvalRunInfo(runId);
  const batchId = runInfo?.batch;
  const { data: batchIdToExtras } = useBatchExtrasQueries(batchId ? [batchId] : []);
  const { data: testCasesInfo } = useGetTestCasesInfo(runId);

  const partialCachedRunInfo = useMemo(() => {
    if (_.isNil(runInfo)) {
      const nullState = {
        pendingTaskCount: undefined,
        completedTaskCount: undefined,
        erroredTaskCount: undefined,
      };
      return nullState;
    }

    let pendingTaskCount = undefined;
    let completedTaskCount = undefined;
    let erroredTaskCount = undefined;

    if (!batchId) {
      pendingTaskCount = 0;
      erroredTaskCount = 0;
      completedTaskCount = testCasesInfo?.totalNumTasks;
    } else if (batchId && batchIdToExtras && batchIdToExtras[batchId]) {
      const extras = batchIdToExtras[batchId];
      pendingTaskCount = extras?.pendingTaskCount;
      completedTaskCount = extras?.completedTaskCount;
      erroredTaskCount = extras?.erroredTaskCount;
    }

    return {
      pendingTaskCount,
      completedTaskCount,
      erroredTaskCount,
    };
  }, [batchId, batchIdToExtras, testCasesInfo?.totalNumTasks, runInfo]);

  return {
    partialCachedRunInfo,
  };
}
